import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSubmitContactForm, ContactFormData } from '../api/contacts';
import { useAnalytics } from '../context/AnalyticsContext';
import './Landing.css';
import { useNotifications } from '../context/NotificationContext';

const Partners: React.FC = () => {
  const { trackPageView } = useAnalytics();
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    type: 'Partnership Inquiry',
    company: '',
    message: ''
  });

  useEffect(()=>{
    window.scrollTo(0, 0);
  },[])

  useEffect(() => {
    trackPageView('/partners', 'Partners');
  }, [trackPageView]);

  const [isSuccess, setIsSuccess] = useState(false);
  const { trackEvent } = useAnalytics();
  const { showSuccess, showError } = useNotifications();
  const submitContactForm = useSubmitContactForm();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const result = await submitContactForm.mutateAsync(formData);
      trackEvent('Partnership Inquiry Submitted');

      if (result.success) {
        showSuccess('Thank you for your partnership inquiry! We\'ll get back to you soon.');
        setIsSuccess(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          type: 'Partnership Inquiry',
          company: '',
          message: ''
        });
      } else {
        showError('Failed to send inquiry. Please try again.', new Error('Failed to send inquiry'));
        setIsSuccess(false);
      }
    } catch (error: any) {
      showError('Failed to send inquiry. Please try again.', error);
      setIsSuccess(false);
    }
  };

  return (
    <div className="app">
      <Header />
      
      {/* Main Content */}
      <main className="main-content">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-content">
            <h1 className="hero-title">Partner With Us</h1>
            <p className="hero-subtitle">
              Join our network to make summer camp planning easier for families everywhere
            </p>
          </div>
        </section>

        {/* Partners Content */}
        <section className="partners-content">
          <div className="content-container">
            <div className="partners-intro">
              <h2>Let's Work Together</h2>
              <p>
                We're building a comprehensive platform to connect families with the best summer camp experiences. 
                Whether you're a camp provider, software company, or local organization, we'd love to explore 
                how we can work together to make summer planning easier for families in your community.
              </p>
            </div>

            <div className="partner-types">
              <div className="partner-type">
                <div className="partner-icon">🏕️</div>
                <h3>Camp Providers</h3>
                <p>
                  Get your camps discovered by families actively searching for summer activities. 
                  We help you reach more families and streamline your registration process.
                </p>
              </div>

              <div className="partner-type">
                <div className="partner-icon">💻</div>
                <h3>Software Companies</h3>
                <p>
                  If you're building camp management software, let's explore integration opportunities 
                  to create seamless experiences for both camp providers and families.
                </p>
              </div>

              <div className="partner-type">
                <div className="partner-icon">🏙️</div>
                <h3>Local Organizations</h3>
                <p>
                  Help us bring Aika to your city! We're looking to partner with local organizations 
                  to make summer camp planning easier for families in your community.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="contact-section">
          <div className="contact-container">
            <h2>Get In Touch</h2>
            <p>
              Ready to partner with us? Fill out the form below and we'll get back to you.
            </p>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleInputChange}
                  required 
                  disabled={submitContactForm.isPending}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleInputChange}
                  required 
                  disabled={submitContactForm.isPending}
                />
              </div>

              <div className="form-group">
                <label htmlFor="company">Organization Name *</label>
                <input 
                  type="text" 
                  id="company" 
                  name="company" 
                  value={formData.company}
                  onChange={handleInputChange}
                  required 
                  disabled={submitContactForm.isPending}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Tell us about your organization and how you'd like to partner *</label>
                <textarea 
                  id="message" 
                  name="message" 
                  value={formData.message}
                  onChange={handleInputChange}
                  required 
                  rows={5}
                  disabled={submitContactForm.isPending}
                  placeholder="Please describe your organization, current offerings, and how you'd like to work with Aika..."
                />
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  disabled={submitContactForm.isPending || formData.name.trim() === '' || formData.email.trim() === '' || (formData.company || '').trim() === '' || (formData.message || '').trim() === ''}
                  className="btn btn--primary"
                >
                  {submitContactForm.isPending ? 'Sending...' : 'Send Partnership Inquiry'}
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Partners;
